import { isMac } from '@/utils/capabilities';

import { sandboxTerminalPanelStore } from './sandbox-terminal-panel.store';

// ---- who owns the tab shortcuts --------------------------------------------
//
// cmd+t / cmd+n / cmd+w / cmd+1..9 mean "chat tab" (or "new chat") in the app
// and "terminal tab" in the dock. Exactly one side may act on a press. Event propagation alone can't decide
// it: the app's hotkeys listen on document, and there are moments when the dock
// is in use but no element inside it has focus (a tab swap hides the focused
// xterm textarea -> activeElement falls back to body; the tab strip or find bar
// is focused; the shell is still spawning). So the dock records ownership from
// focus and pointer events, and the app's tab hotkeys consult it.

export const LOCAL_TERMINAL_DOCK_TEST_ID = 'local-terminal-panel';

let dockOwnsKeyboard = false;

/** Set by the dock from its focus/pointer events; cleared when it hides. */
export function setTerminalKeyboardOwner(owns: boolean): void {
  dockOwnsKeyboard = owns;
}

/** True when a tab shortcut should go to the terminal, not the chat tabs. */
export function terminalOwnsKeyboard(): boolean {
  return dockOwnsKeyboard;
}

/** Whether `target` is inside the local terminal dock's DOM. */
export function isInsideTerminalDock(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false;
  const element =
    target instanceof Element ? target : (target.parentElement ?? null);
  return (
    element?.closest(`[data-testid="${LOCAL_TERMINAL_DOCK_TEST_ID}"]`) !== null
  );
}

/**
 * For the app's tab and new-chat hotkeys (`ignoreEventWhen`): skip when the
 * terminal owns the keyboard or the key was pressed inside the dock.
 */
export function terminalClaimsTabKey(event: KeyboardEvent): boolean {
  return terminalOwnsKeyboard() || isInsideTerminalDock(event.target);
}

/**
 * Tab shortcuts while a local terminal has focus. cmd on mac, ctrl+shift elsewhere
 * (plain ctrl+<letter> is the shell's: ctrl+t transposes, ctrl+w kills a word).
 *
 *   new tab      cmd+t, cmd+n     ctrl+shift+t, ctrl+shift+n
 *   close tab    cmd+w            ctrl+shift+w
 *   next / prev  cmd+shift+] / [  ctrl+tab / ctrl+shift+tab
 *   jump         cmd+1..9         alt+1..9
 *
 * cmd+n is the app's "new chat" everywhere else; in the dock it means what
 * it means in every terminal app: new tab.
 *
 * Returns true when the event was a tab command (and has been handled).
 */
export function handleLocalTerminalKey(event: KeyboardEvent): boolean {
  const store = sandboxTerminalPanelStore;
  const mac = isMac();
  // "primary" = the plain new/close chord: cmd (no shift) on mac, ctrl+shift elsewhere
  const primary = mac
    ? event.metaKey && !event.shiftKey && !event.ctrlKey && !event.altKey
    : event.ctrlKey && event.shiftKey && !event.metaKey && !event.altKey;
  const key = event.key.toLowerCase();

  if (primary && (key === 't' || key === 'n')) {
    store.newTab();
    return true;
  }
  if (primary && key === 'w') {
    const tab = store.activeTab;
    if (tab) store.requestKill(tab.key);
    return true;
  }

  if (mac) {
    if (event.metaKey && event.shiftKey && !event.ctrlKey && !event.altKey) {
      if (event.code === 'BracketRight') {
        store.activateAdjacentTab(1);
        return true;
      }
      if (event.code === 'BracketLeft') {
        store.activateAdjacentTab(-1);
        return true;
      }
    }
    if (event.metaKey && !event.shiftKey && !event.ctrlKey && !event.altKey) {
      const n = digit(event.code);
      if (n !== null) {
        store.activateTabAt(n);
        return true;
      }
    }
    return false;
  }

  if (event.ctrlKey && !event.metaKey && !event.altKey && key === 'tab') {
    store.activateAdjacentTab(event.shiftKey ? -1 : 1);
    return true;
  }
  if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
    const n = digit(event.code);
    if (n !== null) {
      store.activateTabAt(n);
      return true;
    }
  }
  return false;
}

function digit(code: string): number | null {
  const m = /^Digit([1-9])$/.exec(code);
  return m ? Number(m[1]) : null;
}
